import type { CallAudioOutputDevice } from '../../../services/voice-call/native-audio-route'

type RouteKind = CallAudioOutputDevice['kind']

/**
 * How long a just-reported loud-speaker route is held back from the UI while
 * the platform settles the call route. Matches the arbiter's correction-echo
 * window (ROUTE_CORRECTION_ECHO_MS): within it a speaker report during connect
 * is a known transient (CallKit/Telecom first-route default, ringtone
 * loudspeaker leftover, WebRTC re-route) that the audio stack corrects.
 */
export const TRANSIENT_SPEAKER_HOLD_MS = 1200

/**
 * Display-layer gate for the speaker-button flash at connect (owner report,
 * 2026-11-03): the OS briefly reports the loud speaker before CallKit/Telecom
 * settle on the earpiece, so the speaker button painted green then off.
 *
 * The gate holds back a speaker route that (a) the arbiter does not desire and
 * (b) the user did not just pick, until it has persisted for
 * {@link TRANSIENT_SPEAKER_HOLD_MS}. A genuine speaker route (video call
 * default, user toggle, OEM forcing speaker) still shows — at worst ~1.2s
 * late. Non-speaker routes (earpiece / bluetooth / wired / car) always pass
 * through immediately, so external-device UX is untouched.
 *
 * Display only: it never touches the actual route, the arbiter, or any apply
 * path — the audio itself is owned by the native stack.
 */
export class TransientSpeakerGate {
  private speakerSinceMs: number | null = null
  private lastShownKind: RouteKind | null = null

  /**
   * The route kind the UI should render for a reported `kind`.
   * `desiredSpeakerOn` = arbiter desire; `userPickPending` = an optimistic
   * user pick is being displayed (never second-guess the user).
   */
  displayKind(
    kind: RouteKind,
    desiredSpeakerOn: boolean,
    userPickPending: boolean,
    nowMs: number,
  ): RouteKind {
    if (kind !== 'speaker') {
      this.speakerSinceMs = null
      this.lastShownKind = kind
      return kind
    }
    if (desiredSpeakerOn || userPickPending) {
      this.speakerSinceMs = null
      this.lastShownKind = 'speaker'
      return 'speaker'
    }
    if (this.speakerSinceMs === null) this.speakerSinceMs = nowMs
    if (nowMs - this.speakerSinceMs >= TRANSIENT_SPEAKER_HOLD_MS) {
      // Persisted: it is the real route — show it.
      this.lastShownKind = 'speaker'
      return 'speaker'
    }
    // Young + undesired: keep the last settled kind (earpiece before the
    // first report — the pre-connect default on a voice call).
    return this.lastShownKind ?? 'earpiece'
  }

  /**
   * When a speaker report is currently held back, how long (ms) until it
   * should be re-evaluated; null when nothing is held.
   */
  nextCheckDelayMs(nowMs: number): number | null {
    if (this.speakerSinceMs === null) return null
    const remaining = TRANSIENT_SPEAKER_HOLD_MS - (nowMs - this.speakerSinceMs)
    return remaining > 0 ? remaining : null
  }
}
